import { axiosClient } from "../api/axiosClient";
import type { PublicSocialPreviewData } from "gsintegrations";

export type SocialVisibility = "PRIVATE" | "TERREIRO" | "COMMUNITY";
export type ExperienceCategory =
  | "DESENVOLVIMENTO"
  | "GIRA"
  | "INCORPORACAO"
  | "APRENDIZADO"
  | "SENTIMENTO"
  | "ANCESTRALIDADE"
  | "ESPIRITUALIDADE"
  | "DUVIDA_DESCOBERTA"
  | "OTHER";
export type ExperienceReactionType =
  | "IDENTIFIED"
  | "LEARNED"
  | "WELCOMED"
  | "CURIOUS";
export type SocialReportReason =
  | "HARASSMENT"
  | "HATE"
  | "DISCRIMINATION"
  | "RELIGIOUS_INTOLERANCE"
  | "SEXUAL_CONTENT"
  | "VIOLENCE"
  | "SPAM"
  | "IMPERSONATION"
  | "MISINFORMATION"
  | "OTHER";

export type SocialAuthor = {
  userId: string;
  username?: string;
  displayName?: string | null;
  avatarUrl?: string | null;
  traditionLabel?: string | null;
};

export type Experience = {
  id: string;
  authorId: string;
  author?: SocialAuthor;
  title?: string | null;
  content: string;
  traditionLabel?: string | null;
  category?: ExperienceCategory;
  locationLabel?: string | null;
  imageUrl?: string | null;
  visibility?: SocialVisibility;
  reactionCount?: number;
  alsoExperiencedCount?: number;
  commentCount?: number;
  bookmarkCount?: number;
  viewerHasAlsoExperienced?: boolean;
  viewerHasBookmarked?: boolean;
  viewerReactions?: ExperienceReactionType[];
  eventId?: string | null;
  terreiroId?: string | null;
  publishedAt?: string | null;
  editedAt?: string | null;
  createdAt?: string;
};

export type ExperienceComment = {
  id: string;
  experienceId: string;
  authorId: string;
  author?: SocialAuthor;
  body: string;
  parentId?: string | null;
  replyToCommentId?: string | null;
  replyCount?: number;
  likeCount?: number;
  viewerHasLiked?: boolean;
  replies?: ExperienceComment[];
  repliesHasMore?: boolean;
  editedAt?: string | null;
  createdAt?: string;
};

export type Question = {
  id: string;
  authorId?: string | null;
  author?: SocialAuthor;
  title: string;
  description?: string | null;
  traditionLabel?: string | null;
  category?: ExperienceCategory | null;
  source?: "COMMUNITY" | "AI" | "SYSTEM";
  visibility?: SocialVisibility;
  answerCount?: number;
  bookmarkCount?: number;
  reactionCount?: number;
  shareCount?: number;
  viewerHasBookmarked?: boolean;
  viewerReactions?: ExperienceReactionType[];
  createdAt?: string;
};

export type QuestionAnswer = {
  id: string;
  questionId: string;
  authorId: string;
  author?: SocialAuthor;
  content: string;
  traditionLabel?: string | null;
  visibility?: SocialVisibility;
  createdAt?: string;
};

export type SocialProfile = {
  id?: string;
  userId: string;
  username: string;
  displayName?: string | null;
  bio?: string | null;
  traditionLabel?: string | null;
  city?: string | null;
  state?: string | null;
  region?: string | null;
  locationGranularity?: "CITY" | "STATE" | "REGION" | "HIDDEN";
  profileVisibility?: SocialVisibility;
  traditionVisible?: boolean;
  locationVisible?: boolean;
  experiencesVisible?: boolean;
  connectionsVisible?: boolean;
  activityVisible?: boolean;
  avatarUrl?: string | null;
  bannerUrl?: string | null;
  bannerGradientKey?:
    | "oxala-luz"
    | "iemanja-mar"
    | "oxum-aguas-douradas"
    | "ogum-forja-caminhos"
    | "xango-fogo-terra"
    | "iansa-ventos"
    | "oxossi-mata"
    | "nana-terra-aguas"
    | null;
  customBannerGradients?: CustomBannerGradientPreset[];
  selectedCustomBannerGradientId?: string | null;
  website?: string | null;
  verified?: boolean;
  birthDate?: string | null;
  followingCount?: number;
  followersCount?: number;
  pendingFollowersCount?: number;
  joinedLabel?: string | null;
  journeyStartedAt?: string | null;
  onboardingCompletedAt?: string | null;
  verifiedUntil?: string | null;
  connectionStatus?: "NONE" | "PENDING" | "CONNECTED" | "SELF";
  connectionDirection?: "OUTBOUND" | "INBOUND";
};

export type SocialVerificationProgress = {
  activeDays7: number;
  daysRequired: number;
  posts30: number;
  postsRequired: number;
  verified: boolean;
  verifiedUntil: string | null;
  presenceDayKeys7?: string[];
  daysRemaining?: number | null;
  missing: {
    days: number;
    posts: number;
  };
};

export type SocialPresencePingResponse = {
  progress: SocialVerificationProgress;
  granted: boolean;
  extended: boolean;
  dayRecorded: boolean;
};

export type CustomBannerGradientPreset = {
  id: string;
  name: string;
  colors: [string, string] | [string, string, string];
};

export type SimilarPerson = {
  userId: string;
  username?: string;
  displayName?: string | null;
  avatarUrl?: string | null;
  traditionLabel?: string | null;
  /** Affinity on 0–100 percent scale (API); UI clamps display to 1–100. */
  affinity?: number;
  sharedCategory?: ExperienceCategory | null;
};

export type CursorPage<T> = {
  items: T[];
  nextCursor: string | null;
  hasMore?: boolean;
};

export type SocialMessage = {
  id: string;
  conversationId: string;
  senderId: string;
  clientMessageId: string;
  content: string;
  createdAt: string;
  updatedAt: string;
  deliveryStatus?: "SENDING" | "SENT" | "READ" | "FAILED";
};

export type SocialConversation = {
  id: string;
  participantUserIds: string[];
  peer: {
    userId: string;
    username?: string;
    displayName?: string | null;
    avatarUrl?: string | null;
  };
  lastReadAt: string | null;
  unreadCount?: number;
  canSend: boolean;
  lastMessage?: SocialMessage | null;
  createdAt: string;
  updatedAt: string;
};

export type MarkSocialConversationReadResult = {
  conversationId: string;
  readAt: string;
};

export type CreateExperienceInput = {
  content: string;
  title?: string;
  traditionLabel?: string;
  category?: ExperienceCategory;
  locationLabel?: string;
  imageUrl?: string;
  visibility?: SocialVisibility;
  terreiroId?: string;
  eventId?: string;
};

export type CreateQuestionInput = {
  title: string;
  description?: string;
  traditionLabel?: string;
  category?: ExperienceCategory;
  visibility?: SocialVisibility;
  terreiroId?: string;
  eventId?: string;
};

export type PatchSocialProfileInput = {
  username?: string;
  displayName?: string | null;
  bio?: string | null;
  traditionLabel?: string | null;
  religionId?: string | null;
  city?: string | null;
  state?: string | null;
  region?: string | null;
  locationGranularity?: "CITY" | "STATE" | "REGION" | "HIDDEN";
  profileVisibility?: SocialVisibility;
  traditionVisible?: boolean;
  locationVisible?: boolean;
  experiencesVisible?: boolean;
  connectionsVisible?: boolean;
  activityVisible?: boolean;
  avatarUrl?: string | null;
  bannerUrl?: string | null;
  bannerGradientKey?: string | null;
  customBannerGradients?: CustomBannerGradientPreset[];
  selectedCustomBannerGradientId?: string | null;
  website?: string | null;
  birthDate?: string | null;
  completeOnboarding?: boolean;
};

export type UnsplashBannerPhoto = {
  id: string;
  width: number | null;
  height: number | null;
  urls: {
    regular: string;
    small: string;
    raw: string;
  };
  alt: string | null;
  description: string | null;
  photographer: {
    name: string;
    profileUrl: string;
  };
  downloadLocation: string;
};

export type UnsplashBannerSearchResult = {
  items: UnsplashBannerPhoto[];
  total: number | null;
  totalPages: number | null;
  page: number;
  perPage: number;
};

export type SocialSearchResult = {
  experiences?: Experience[];
  questions?: Question[];
  people?: SimilarPerson[];
};

export type PublicSocialPreview = PublicSocialPreviewData & {
  deepLinkPath?: string;
};

type PublicSocialProfileDto = {
  userId: string;
  username: string;
  displayName: string | null;
  bio: string | null;
  avatarUrl: string | null;
  bannerUrl: string | null;
  traditionLabel?: string | null;
  location?: {
    granularity: "CITY" | "STATE" | "REGION";
    value: string;
  };
  connectionsCount?: number;
  joinedAt: string;
};

export type PublicSocialPreviewKind = "EXPERIENCE" | "QUESTION" | "PROFILE";

export const socialQueryKeys = {
  all: ["social"] as const,
  feed: (params?: Record<string, unknown>) =>
    [...socialQueryKeys.all, "feed", params] as const,
  experiences: (params?: Record<string, unknown>) =>
    [...socialQueryKeys.all, "experiences", params] as const,
  experience: (id: string) =>
    [...socialQueryKeys.all, "experience", id] as const,
  authorExperiences: (authorId: string, params?: Record<string, unknown>) =>
    [...socialQueryKeys.all, "author-experiences", authorId, params] as const,
  similarExperiences: (id: string) =>
    [...socialQueryKeys.all, "similar-experiences", id] as const,
  questions: (params?: Record<string, unknown>) =>
    [...socialQueryKeys.all, "questions", params] as const,
  question: (id: string) => [...socialQueryKeys.all, "question", id] as const,
  answers: (id: string) => [...socialQueryKeys.all, "answers", id] as const,
  comments: (id: string) => [...socialQueryKeys.all, "comments", id] as const,
  saved: (params?: Record<string, unknown>) =>
    [...socialQueryKeys.all, "saved", params] as const,
  savedQuestions: (params?: Record<string, unknown>) =>
    [...socialQueryKeys.all, "saved-questions", params] as const,
  connections: (params?: Record<string, unknown>) =>
    [...socialQueryKeys.all, "connections", params] as const,
  similarPeople: (experienceId?: string) =>
    [...socialQueryKeys.all, "similar-people", experienceId] as const,
  profileMe: () => [...socialQueryKeys.all, "profile", "me"] as const,
  verificationMe: () =>
    [...socialQueryKeys.all, "verification", "me"] as const,
  profile: (username: string) =>
    [...socialQueryKeys.all, "profile", username] as const,
  unsplashBanners: (query: string) =>
    [...socialQueryKeys.all, "unsplash-banners", query] as const,
  publicPreview: (kind: PublicSocialPreviewKind, identifier: string) =>
    [...socialQueryKeys.all, "public-preview", kind, identifier] as const,
  search: (params?: Record<string, unknown>) =>
    [...socialQueryKeys.all, "search", params] as const,
  conversations: () => [...socialQueryKeys.all, "conversations"] as const,
  conversation: (conversationId: string) =>
    [...socialQueryKeys.conversations(), conversationId] as const,
  messages: (conversationId: string) =>
    [...socialQueryKeys.conversation(conversationId), "messages"] as const,
};

const withCreds = { withCredentials: true as const };

export type SocialFeedScope = "for_you" | "following";

export type SocialFeedItem =
  | {
      kind: "experience";
      id: string;
      createdAt?: string;
      experience: Experience;
    }
  | {
      kind: "question";
      id: string;
      createdAt?: string;
      question: Question;
    };

function normalizeFeedItem(raw: unknown): SocialFeedItem | null {
  if (!raw || typeof raw !== "object") return null;
  const item = raw as Record<string, unknown>;
  if (item.kind === "question" && item.question && typeof item.id === "string") {
    return {
      kind: "question",
      id: item.id,
      createdAt: item.createdAt as string | undefined,
      question: item.question as Question,
    };
  }
  if (
    item.kind === "experience" &&
    item.experience &&
    typeof item.id === "string"
  ) {
    return {
      kind: "experience",
      id: item.id,
      createdAt: item.createdAt as string | undefined,
      experience: item.experience as Experience,
    };
  }
  // Legacy flat experience payload
  if (typeof item.id === "string" && typeof item.content === "string") {
    return {
      kind: "experience",
      id: item.id,
      createdAt: item.createdAt as string | undefined,
      experience: item as unknown as Experience,
    };
  }
  return null;
}

export const socialRepository = {
  async getFeed(params?: {
    cursor?: string;
    limit?: number;
    scope?: SocialFeedScope;
    visibility?: SocialVisibility;
  }) {
    const { data } = await axiosClient.get<CursorPage<unknown>>(
      "/social/feed",
      { params, ...withCreds }
    );
    return {
      ...data,
      items: (data.items ?? [])
        .map(normalizeFeedItem)
        .filter((item): item is SocialFeedItem => item != null),
    };
  },

  async listExperiences(params?: {
    cursor?: string;
    limit?: number;
    scope?: SocialFeedScope;
    authorId?: string;
  }) {
    const { data } = await axiosClient.get<CursorPage<Experience>>(
      "/social/experiences",
      { params, ...withCreds }
    );
    return data;
  },

  async listExperiencesByAuthor(
    authorId: string,
    params?: { cursor?: string; limit?: number }
  ) {
    return this.listExperiences({ ...params, authorId });
  },

  async createExperience(body: CreateExperienceInput) {
    const { data } = await axiosClient.post<Experience>(
      "/social/experiences",
      body,
      withCreds
    );
    return data;
  },

  async getExperience(id: string) {
    const { data } = await axiosClient.get<Experience>(
      `/social/experiences/${id}`,
      withCreds
    );
    return data;
  },

  async updateExperience(
    id: string,
    body: Omit<Partial<CreateExperienceInput>, "title"> & {
      title?: string | null;
    }
  ) {
    const { data } = await axiosClient.patch<Experience>(
      `/social/experiences/${id}`,
      body,
      withCreds
    );
    return data;
  },

  async deleteExperience(id: string) {
    const { data } = await axiosClient.delete(
      `/social/experiences/${id}`,
      withCreds
    );
    return data;
  },

  async addReaction(id: string, type: ExperienceReactionType) {
    const { data } = await axiosClient.post(
      `/social/experiences/${id}/reactions`,
      { type },
      withCreds
    );
    return data;
  },

  async removeReaction(id: string, type: ExperienceReactionType) {
    const { data } = await axiosClient.delete(
      `/social/experiences/${id}/reactions/${type}`,
      withCreds
    );
    return data;
  },

  async alsoExperienced(id: string) {
    const { data } = await axiosClient.post(
      `/social/experiences/${id}/also-experienced`,
      {},
      withCreds
    );
    return data;
  },

  async addBookmark(id: string) {
    const { data } = await axiosClient.post(
      `/social/experiences/${id}/bookmark`,
      {},
      withCreds
    );
    return data;
  },

  async removeBookmark(id: string) {
    const { data } = await axiosClient.delete(
      `/social/experiences/${id}/bookmark`,
      withCreds
    );
    return data;
  },

  async listSaved(params?: { cursor?: string; limit?: number }) {
    const { data } = await axiosClient.get<CursorPage<Experience>>(
      `/social/experiences/saved`,
      { params, ...withCreds }
    );
    return data;
  },

  async addQuestionBookmark(id: string) {
    const { data } = await axiosClient.post(
      `/social/questions/${id}/bookmark`,
      {},
      withCreds
    );
    return data;
  },

  async removeQuestionBookmark(id: string) {
    const { data } = await axiosClient.delete(
      `/social/questions/${id}/bookmark`,
      withCreds
    );
    return data;
  },

  async addQuestionReaction(id: string, type: ExperienceReactionType) {
    const { data } = await axiosClient.post(
      `/social/questions/${id}/reactions`,
      { type },
      withCreds
    );
    return data;
  },

  async removeQuestionReaction(id: string, type: ExperienceReactionType) {
    const { data } = await axiosClient.delete(
      `/social/questions/${id}/reactions/${type}`,
      withCreds
    );
    return data;
  },

  async listSavedQuestions(params?: { cursor?: string; limit?: number }) {
    const { data } = await axiosClient.get<CursorPage<Question>>(
      `/social/questions/saved`,
      { params, ...withCreds }
    );
    return data;
  },

  async listComments(
    id: string,
    params?: { cursor?: string; limit?: number }
  ) {
    const { data } = await axiosClient.get<CursorPage<ExperienceComment>>(
      `/social/experiences/${id}/comments`,
      { params, ...withCreds }
    );
    return data;
  },

  async listCommentReplies(
    experienceId: string,
    commentId: string,
    params?: { cursor?: string; limit?: number }
  ) {
    const { data } = await axiosClient.get<CursorPage<ExperienceComment>>(
      `/social/experiences/${experienceId}/comments/${commentId}/replies`,
      { params, ...withCreds }
    );
    return data;
  },

  async updateComment(experienceId: string, commentId: string, body: string) {
    const { data } = await axiosClient.patch<ExperienceComment>(
      `/social/experiences/${experienceId}/comments/${commentId}`,
      { body },
      withCreds
    );
    return data;
  },

  async deleteComment(experienceId: string, commentId: string) {
    const { data } = await axiosClient.delete(
      `/social/experiences/${experienceId}/comments/${commentId}`,
      withCreds
    );
    return data;
  },

  async createComment(
    id: string,
    body: {
      body: string;
      parentId?: string;
      replyToCommentId?: string;
    }
  ) {
    const { data } = await axiosClient.post<ExperienceComment>(
      `/social/experiences/${id}/comments`,
      body,
      withCreds
    );
    return data;
  },

  async likeComment(experienceId: string, commentId: string) {
    const { data } = await axiosClient.post<{
      commentId: string;
      liked: boolean;
      likeCount: number;
    }>(
      `/social/experiences/${experienceId}/comments/${commentId}/like`,
      {},
      withCreds
    );
    return data;
  },

  async unlikeComment(experienceId: string, commentId: string) {
    const { data } = await axiosClient.delete<{
      commentId: string;
      liked: boolean;
      likeCount: number;
    }>(
      `/social/experiences/${experienceId}/comments/${commentId}/like`,
      withCreds
    );
    return data;
  },

  async getSimilarExperiences(id: string) {
    const { data } = await axiosClient.get<{ items: Experience[] }>(
      `/social/experiences/${id}/similar`,
      withCreds
    );
    return data;
  },

  async listQuestions(params?: { cursor?: string; limit?: number }) {
    const { data } = await axiosClient.get<CursorPage<Question>>(
      "/social/questions",
      { params, ...withCreds }
    );
    return data;
  },

  async createQuestion(body: CreateQuestionInput) {
    const { data } = await axiosClient.post<Question>(
      "/social/questions",
      body,
      withCreds
    );
    return data;
  },

  async getQuestion(id: string) {
    const { data } = await axiosClient.get<Question>(
      `/social/questions/${id}`,
      withCreds
    );
    return data;
  },

  async listAnswers(id: string) {
    const { data } = await axiosClient.get<{ items: QuestionAnswer[] }>(
      `/social/questions/${id}/answers`,
      withCreds
    );
    return data;
  },

  async answerQuestion(
    id: string,
    body: {
      content: string;
      traditionLabel?: string;
      visibility?: SocialVisibility;
    }
  ) {
    const { data } = await axiosClient.post<QuestionAnswer>(
      `/social/questions/${id}/answers`,
      body,
      withCreds
    );
    return data;
  },

  async getSimilarPeople(experienceId?: string) {
    const { data } = await axiosClient.get<{ items: SimilarPerson[] }>(
      "/social/people/similar",
      { params: experienceId ? { experienceId } : undefined, ...withCreds }
    );
    return data;
  },

  async getMyProfile() {
    const { data } = await axiosClient.get<SocialProfile>(
      "/social/profile/me",
      withCreds
    );
    return data;
  },

  async getMyVerificationProgress() {
    const { data } = await axiosClient.get<SocialVerificationProgress>(
      "/social/me/verification",
      withCreds
    );
    return data;
  },

  async pingSocialPresence() {
    const { data } = await axiosClient.post<SocialPresencePingResponse>(
      "/social/presence",
      {},
      withCreds
    );
    return data;
  },

  async getProfileByUsername(username: string) {
    const { data } = await axiosClient.get<SocialProfile>(
      `/social/profile/${username}`,
      withCreds
    );
    return data;
  },

  async openConversation(participantUserId: string) {
    const { data } = await axiosClient.post<SocialConversation>(
      "/social/conversations",
      { participantUserId },
      withCreds
    );
    return data;
  },

  async listConversations(params?: { cursor?: string; limit?: number }) {
    const { data } = await axiosClient.get<CursorPage<SocialConversation>>(
      "/social/conversations",
      { params, ...withCreds }
    );
    return data;
  },

  async getConversation(conversationId: string) {
    const { data } = await axiosClient.get<SocialConversation>(
      `/social/conversations/${conversationId}`,
      withCreds
    );
    return data;
  },

  async listMessages(
    conversationId: string,
    params?: { cursor?: string; limit?: number }
  ) {
    const { data } = await axiosClient.get<CursorPage<SocialMessage>>(
      `/social/conversations/${conversationId}/messages`,
      { params, ...withCreds }
    );
    return data;
  },

  async sendMessage(
    conversationId: string,
    body: { clientMessageId: string; content: string }
  ) {
    const { data } = await axiosClient.post<SocialMessage>(
      `/social/conversations/${conversationId}/messages`,
      body,
      withCreds
    );
    return data;
  },

  async markConversationRead(conversationId: string, messageId: string) {
    const { data } = await axiosClient.patch<MarkSocialConversationReadResult>(
      `/social/conversations/${conversationId}/read`,
      { messageId },
      withCreds
    );
    return data;
  },

  async patchProfile(body: PatchSocialProfileInput) {
    const { data } = await axiosClient.patch<SocialProfile>(
      "/social/profile",
      body,
      withCreds
    );
    return data;
  },

  async uploadProfileAvatar(file: File) {
    const form = new FormData();
    form.append("avatar", file, file.name);
    const { data } = await axiosClient.post<SocialProfile>(
      "/social/profile/avatar",
      form,
      {
        headers: { "Content-Type": "multipart/form-data" },
        ...withCreds,
      }
    );
    return data;
  },

  async uploadProfileBanner(file: File) {
    const form = new FormData();
    form.append("banner", file, file.name);
    const { data } = await axiosClient.post<SocialProfile>(
      "/social/profile/banner",
      form,
      {
        headers: { "Content-Type": "multipart/form-data" },
        ...withCreds,
      }
    );
    return data;
  },

  async searchUnsplashBanners(
    query: string,
    params?: { page?: number; perPage?: number }
  ) {
    const { data } = await axiosClient.get<UnsplashBannerSearchResult>(
      "/social/profile/banners/unsplash",
      {
        params: {
          query,
          page: params?.page ?? 1,
          perPage: params?.perPage ?? 12,
        },
        ...withCreds,
      }
    );
    return data;
  },

  async trackUnsplashBannerSelection(photoId: string) {
    const { data } = await axiosClient.post<{ tracked: true }>(
      "/social/profile/banners/unsplash/track",
      { photoId },
      withCreds
    );
    return data;
  },

  async connect(userId: string) {
    const { data } = await axiosClient.post(
      `/social/connections/${userId}`,
      {},
      withCreds
    );
    return data;
  },

  async listConnections(params: {
    username?: string;
    tab?: "following" | "followers" | "requests";
    q?: string;
    cursor?: string;
    limit?: number;
  }) {
    const { username, ...query } = params;
    const path = username
      ? `/social/profile/${encodeURIComponent(username)}/connections`
      : `/social/profile/me/connections`;
    const { data } = await axiosClient.get<{
      items: Array<{
        userId: string;
        username?: string;
        displayName?: string | null;
        avatarUrl?: string | null;
        traditionLabel?: string | null;
        connectionStatus: "CONNECTED" | "PENDING";
        connectionDirection?: "OUTBOUND" | "INBOUND";
      }>;
      nextCursor?: string | null;
      hasMore?: boolean;
      username: string;
      followingCount: number;
      followersCount: number;
      pendingFollowersCount?: number;
    }>(path, { params: query, ...withCreds });
    return data;
  },

  async disconnect(userId: string) {
    const { data } = await axiosClient.delete(
      `/social/connections/${userId}`,
      withCreds
    );
    return data;
  },

  async createReport(body: {
    targetType: string;
    targetId: string;
    reason: SocialReportReason;
    details?: string;
  }) {
    const { data } = await axiosClient.post("/social/reports", body, withCreds);
    return data;
  },

  async createBlock(userId: string) {
    const { data } = await axiosClient.post(
      "/social/blocks",
      { userId },
      withCreds
    );
    return data;
  },

  async removeBlock(userId: string) {
    const { data } = await axiosClient.delete(
      `/social/blocks/${userId}`,
      withCreds
    );
    return data;
  },

  async createShareEvent(body: {
    targetType: string;
    targetId: string;
    channel?: "WHATSAPP" | "LINK" | "OTHER";
  }) {
    const { data } = await axiosClient.post(
      "/social/share-events",
      body,
      withCreds
    );
    return data;
  },

  async search(params: {
    q?: string;
    type?: "experiences" | "questions" | "people" | "all";
    tradition?: string;
    category?: ExperienceCategory;
    location?: string;
    limit?: number;
  }) {
    const { data } = await axiosClient.get<SocialSearchResult>(
      "/social/search",
      {
        params: {
          q: params.q,
          type: params.type,
          traditionLabel: params.tradition,
          category: params.category,
          limit: params.limit,
        },
        ...withCreds,
      }
    );
    return data;
  },

  async createKnowledge(body: {
    title: string;
    content: string;
    traditionLabel?: string;
    topicKey?: string;
    visibility?: SocialVisibility;
    terreiroId?: string;
  }) {
    const { data } = await axiosClient.post(
      "/social/knowledge",
      body,
      withCreds
    );
    return data;
  },

  async getKnowledge(id: string) {
    const { data } = await axiosClient.get(
      `/social/knowledge/${id}`,
      withCreds
    );
    return data;
  },

  async getPublicExperiencePreview(id: string): Promise<PublicSocialPreview> {
    const { data } = await axiosClient.get<PublicSocialPreview>(
      `/social/public/experiences/${encodeURIComponent(id)}`,
    );
    return data;
  },

  async getPublicQuestionPreview(id: string): Promise<PublicSocialPreview> {
    const { data } = await axiosClient.get<PublicSocialPreview>(
      `/social/public/questions/${encodeURIComponent(id)}`,
    );
    return data;
  },

  async getPublicProfilePreview(
    username: string,
  ): Promise<PublicSocialPreview> {
    const { data } = await axiosClient.get<PublicSocialProfileDto>(
      `/social/public/profiles/${encodeURIComponent(username)}`,
    );
    return {
      type: "PROFILE",
      id: data.userId,
      username: data.username,
      displayName: data.displayName,
      avatarUrl: data.avatarUrl,
      bio: data.bio,
      traditionLabel: data.traditionLabel,
      locationLabel: data.location?.value,
      joinedLabel: data.joinedAt,
      connectionCount: data.connectionsCount,
      cta: "Entrar para conectar",
    };
  },

  async getPublicPreview(
    kind: PublicSocialPreviewKind,
    identifier: string,
  ): Promise<PublicSocialPreview> {
    if (kind === "EXPERIENCE") {
      return this.getPublicExperiencePreview(identifier);
    }
    if (kind === "QUESTION") {
      return this.getPublicQuestionPreview(identifier);
    }
    return this.getPublicProfilePreview(identifier);
  },
};
